import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';

export interface GetProductMetricsResponseRecord {
  retailerId: number;
  retailerSku: string;
  stacklineSku: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
  otherTraffic: number;
  paidAdSpend: number;
  paidAdSales: number;
}

export interface GetProductMetricsResponse {
  records: GetProductMetricsResponseRecord[];
}

export interface GetEntityMetadataResponseRecord {
  id: string;
  title: string;
  subtitle: string;
  translatedTitles: string;
  brandName: string;
  brandId: number;
  categoryName: string;
  categoryId: number;
  subCategoryName: string;
  subCategoryId: number;
  retailerSku: string;
  stacklineSku: string;
  upc: string;
}

export interface GetTotalTrafficByProductResponseRecord extends GetProductMetricsResponseRecord {
  product: GetEntityMetadataResponseRecord;
}

export interface GetTotalTrafficByProductResponse {
  records: GetTotalTrafficByProductResponseRecord[];
}

/**
 * Returns traffic metrics for topics products
 */
export const useTotalTrafficByProduct = ({ page, itemsPerPage }: { page: number; itemsPerPage: number }) => {
  const retailerId = useAppSelector((state) => state.retailer.id);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const baseRequest = useBaseTopicRequestBuilder();

  const productsRequest = baseRequest
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setSortField('totalTraffic')
    .setRetailerId(+retailerId)
    .build();

  const productsComparisonRequest = new TopicRequestBuilder(productsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [TOPIC_QUERY_KEYS.all, 'totalTrafficProductsGrid', productsRequest, productsComparisonRequest],
    queryFn: async () => {
      const endpoint = '/api/beacontopics/GetProductMetrics';
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetProductMetricsResponse>(endpoint, productsRequest),
        axios.post<GetProductMetricsResponse>(endpoint, productsComparisonRequest)
      ]);

      return [response, comparisonResponse];
    }
  });

  const [response, comparisonResponse] = data ?? [{ records: [] }, { records: [] }];
  const slicedSkus = response.records
    .map((record) => record.stacklineSku)
    .slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Fetch the product metadata by page, otherwise it will be too slow if
  // we fetch them all at once
  const { data: responseWithMetadata, isLoading: metadataLoading } = useQuery({
    queryKey: [TOPIC_QUERY_KEYS.all, 'totalTrafficProductsGridMetadata', slicedSkus],
    queryFn: async () => {
      const { data: productMetadataResponse } = await axios.post<GetEntityMetadataResponseRecord[]>(
        '/api/beacon/GetEntityMetadataInBulk',
        {
          entityType: 'product',
          entityIds: slicedSkus,
          retailerId
        }
      );

      const stacklineSkuToMetadataMap = productMetadataResponse.reduce((acc, record) => {
        acc.set(record.stacklineSku, record);
        return acc;
      }, new Map<string, GetEntityMetadataResponseRecord>());

      const responsesWithMetadataInner: GetTotalTrafficByProductResponse[] = [
        {
          ...response,
          records: response.records.map((record) => ({
            ...record,
            product: stacklineSkuToMetadataMap.get(record.stacklineSku)
          }))
        },
        {
          ...comparisonResponse,
          records: comparisonResponse.records.map((record) => ({
            ...record,
            product: stacklineSkuToMetadataMap.get(record.stacklineSku)
          }))
        }
      ];

      return responsesWithMetadataInner;
    },
    enabled: !isLoading
  });

  return {
    response: responseWithMetadata ?? [{ records: [] }, { records: [] }],
    isLoading: isLoading || metadataLoading
  };
};
